import React from 'react';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatterCurrency } from '../../../utils/methods';

import {
  DashboardArea,
  DashboardColumn,
  BigCard,
  BigCardCenterGroup,
  BigCardTitle,
  BigCardAmountTeal,
  BigCardAmountDark,
  BigCardCount,
  BigCardCompare,
  BigCardCompareText,
  CompareArrow,
  SmallCard,
  SmallCardLabel,
  SmallCardLabelText,
  SmallCardValue,
} from './SalesManagTab1Styled';

const won = value => `${formatterCurrency(value || 0)}원`;
const count = value => `${(Number(value) || 0).toLocaleString('ko-KR')}건`;

// 큰 카드 금액은 한 줄 고정. 자릿수가 많으면(억 단위·음수) 카드 폭을 넘어 '원'이 다음 줄로 꺾였으므로 글자 수로 줄인다.
const AMOUNT_LENGTH_STEPS = [
  { maxLength: 9, scale: 1 },
  { maxLength: 11, scale: 0.8 },
  { maxLength: 13, scale: 0.68 },
];
const MIN_AMOUNT_SCALE = 0.58;
const amountFontSize = (text, baseSize) => {
  const step = AMOUNT_LENGTH_STEPS.find(item => text.length <= item.maxLength);
  return `${Math.round(baseSize * (step ? step.scale : MIN_AMOUNT_SCALE))}px`;
};
const TEAL_AMOUNT_SIZE = 36;
const DARK_AMOUNT_SIZE = 32;

// 증감 방향 화살표 (12x16, Figma). 증가=주황 위쪽, 감소=파랑 아래쪽(상하반전).
// fill=currentColor 로 부모(CompareArrow)의 color 를 받아 색을 주입한다.
const DirectionArrow = ({ direction }) => (
  <CompareArrow direction={direction}>
    <svg width="12" height="16" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 5.89474H8.05273V16H3.9502V5.89474H0L6 0L12 5.89474Z" fill="currentColor" />
    </svg>
  </CompareArrow>
);

// 직전 동일기간 대비 순매출 증감 문구. 기간탭별로 자연어가 달라진다.
// - day:   어제보다 {차액}원
// - week:  지난주보다 {차액}원
// - month: 지난달 {N}월보다 {차액}원 (N = 기준월의 직전 달)
// 차액 0/NaN 이면 화살표 없이 '변동 없음'.
const buildCompare = (change, periodTab, refDate) => {
  const n = Number(change);
  if (!Number.isFinite(n) || n === 0) {
    return { text: '변동 없음', direction: null };
  }

  let prefix;
  if (periodTab === 'week') {
    prefix = '지난주보다';
  } else if (periodTab === 'month') {
    // 기준월의 직전 달 숫자 (예: 기준 5월 → "지난달 4월보다")
    const prevMonth = moment(refDate || undefined).subtract(1, 'month').month() + 1;
    prefix = `지난달 ${prevMonth}월보다`;
  } else {
    prefix = '어제보다';
  }

  return {
    text: `${prefix} ${formatterCurrency(Math.abs(n))}원`,
    direction: n > 0 ? 'up' : 'down',
  };
};

// 결제금액 카드 계산식 툴팁 내용.
// 백엔드 검증: totalAmount = (판매가 − 할인 − 미수금) − 환불 (환불 차감 후 순매출).
const PAYMENT_FORMULA = '결제금액 = 판매가 − 할인 − 미수금 − 환불';

// 우측 대시보드.
// 실데이터(paymentTotal): 결제금액(순매출)/할인/환불/미수금/일일권수익/신규·재등록 회원/직전기간 대비.
// 양도 수수료: totalPayMap 에 FEE 필드가 없어 PRODUCT_TYPE summary 의 '양수금' paid 합(transferFeeTotal)으로 채운다.
// 결제예정 금액: 구독 자동결제 예정 총액. totalPayMap.totalScheduledAmount/totalScheduledCount(기간 기준, 행 필터 무관)으로 채운다.
const SalesManagDashboard = ({ paymentTotal, totalCount, transferFeeTotal, periodTab, refDate }) => {
  const compare = buildCompare(paymentTotal?.totalAmountChange, periodTab, refDate);
  return (
    <DashboardArea>
      {/* Column 1 — 결제금액(대형) / 할인금액 / 결제예정 금액 */}
      <DashboardColumn grow={380}>
        <BigCard variant="teal">
          <BigCardCenterGroup>
            <BigCardTitle>
              결제금액
              <Tooltip title={PAYMENT_FORMULA} placement="top" arrow>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#737373', ml: '4px', verticalAlign: 'middle' }} />
              </Tooltip>
            </BigCardTitle>
            <BigCardAmountTeal sx={{ fontSize: amountFontSize(won(paymentTotal?.totalAmount), TEAL_AMOUNT_SIZE) }}>
              {won(paymentTotal?.totalAmount)}
            </BigCardAmountTeal>
            <BigCardCount>{(totalCount || 0).toLocaleString('ko-KR')}건</BigCardCount>
          </BigCardCenterGroup>
          {/* 직전 동일기간 대비 순매출 증감 (기간탭별 자연어 + 방향 화살표) */}
          <BigCardCompare>
            <BigCardCompareText>{compare.text}</BigCardCompareText>
            {compare.direction && <DirectionArrow direction={compare.direction} />}
          </BigCardCompare>
        </BigCard>

        <SmallCard variant="teal">
          <SmallCardLabel>
            <SmallCardLabelText tone="orange">할인 금액</SmallCardLabelText>
          </SmallCardLabel>
          <SmallCardValue>{won(paymentTotal?.totalPaymentDiscountAmount)}</SmallCardValue>
        </SmallCard>

        <SmallCard variant="teal">
          <SmallCardLabel>
            <SmallCardLabelText tone="gray">결제예정 금액</SmallCardLabelText>
            <Tooltip title="조회 기간에 자동결제(구독) 예정인 금액 합계" placement="top" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: '#737373' }} />
            </Tooltip>
          </SmallCardLabel>
          {/* 결제예정 금액: totalPayMap.totalScheduledAmount(없으면 0). 건수는 totalScheduledCount. */}
          <SmallCardValue>
            {won(paymentTotal?.totalScheduledAmount)}
            {Number(paymentTotal?.totalScheduledCount) > 0 && (
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#737373', marginLeft: '6px' }}>
                {count(paymentTotal?.totalScheduledCount)}
              </span>
            )}
          </SmallCardValue>
        </SmallCard>
      </DashboardColumn>

      {/* Column 2 — 신규등록 회원(대형) / 일일권 수익 / 환불금액 */}
      <DashboardColumn grow={338}>
        <BigCard variant="gray">
          <BigCardTitle accent="teal">신규등록 회원</BigCardTitle>
          {/* 신규등록: 회원이 해당 지점에서 처음 결제한 건 기준(타지점 결제 무관) */}
          <BigCardAmountDark sx={{ fontSize: amountFontSize(won(paymentTotal?.totalNewAmount), DARK_AMOUNT_SIZE) }}>
            {won(paymentTotal?.totalNewAmount)}
          </BigCardAmountDark>
          <BigCardCount>{count(paymentTotal?.totalNewCount)}</BigCardCount>
        </BigCard>

        <SmallCard variant="gray">
          <SmallCardLabel>
            <SmallCardLabelText tone="green">일일권 수익</SmallCardLabelText>
          </SmallCardLabel>
          <SmallCardValue>{won(paymentTotal?.totalTrialAmount)}</SmallCardValue>
        </SmallCard>

        <SmallCard variant="gray">
          <SmallCardLabel>
            <SmallCardLabelText tone="orange">환불금액</SmallCardLabelText>
          </SmallCardLabel>
          <SmallCardValue>{won(paymentTotal?.totalRefundAmount)}</SmallCardValue>
        </SmallCard>
      </DashboardColumn>

      {/* Column 3 — 재등록 회원(대형) / 양도 수수료 / 미수금액 */}
      <DashboardColumn grow={338}>
        <BigCard variant="gray">
          <BigCardTitle accent="teal">재등록 회원</BigCardTitle>
          {/* 재등록: 해당 지점 첫 결제 이후 결제(타지점에서 옮겨온 회원·삭제회원 결제 포함) */}
          <BigCardAmountDark sx={{ fontSize: amountFontSize(won(paymentTotal?.totalReAmount), DARK_AMOUNT_SIZE) }}>
            {won(paymentTotal?.totalReAmount)}
          </BigCardAmountDark>
          <BigCardCount>{count(paymentTotal?.totalReCount)}</BigCardCount>
        </BigCard>

        <SmallCard variant="gray">
          <SmallCardLabel>
            <SmallCardLabelText tone="gray">양도 수수료</SmallCardLabelText>
          </SmallCardLabel>
          {/* 양도 수수료: PRODUCT_TYPE summary 의 '양수금'(양도수수료) paid 합 */}
          <SmallCardValue>{won(transferFeeTotal)}</SmallCardValue>
        </SmallCard>

        <SmallCard variant="gray">
          <SmallCardLabel>
            <SmallCardLabelText tone="orange">미수금액</SmallCardLabelText>
          </SmallCardLabel>
          <SmallCardValue>{won(paymentTotal?.totalOutstandingAmount)}</SmallCardValue>
        </SmallCard>
      </DashboardColumn>
    </DashboardArea>
  );
};

export default SalesManagDashboard;
